import React from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import {AnimatePresence} from 'motion/react';

import {transient} from '../../../../utils/emotionTransientProps';
import {ChevronDown} from '../../../icons';
import {P} from '../../../layout/Text';
import {OptionItem} from '../OptionItem';
import {OptionList} from '../OptionList';

import {useSelectInput} from './useSelectInput';

import type {Option} from '../../../../utils/childTypes';

/**
 * The `ComponentProps` interface defines the properties for the `SelectInput` component.
 * This interface specifies all the configuration options needed to create a customizable dropdown selection
 * interface that supports both single and multi-select functionality with accessibility features.
 */
export interface ComponentProps {
    /**
     * The initial value(s) for the select input, which can be a single string for single-select mode or an array of strings for multi-select mode.
     * This value determines which option(s) are pre-selected when the component first renders and serves as the starting point for user interactions.
     * The type flexibility allows the same interface to handle both single and multiple selection scenarios seamlessly.
     */
    defaultValue: string[] | string;
    /**
     * The display label shown in the select input when closed, providing users with context about what they are selecting.
     * This label acts as a placeholder text that describes the purpose or category of the selection options available.
     * It remains visible until the dropdown is opened and helps users understand the input's function at a glance.
     */
    filterLabel: string;
    /**
     * Whether the select input is disabled and non-interactive, preventing any user interactions when set to true.
     * When disabled, the component will not respond to clicks, keyboard navigation, or other user input methods.
     * This property is useful for conditional form states or when certain selections should be temporarily unavailable.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Whether multiple options can be selected simultaneously, enabling multi-select functionality when set to true.
     * In multi-select mode, users can choose several options from the dropdown list, and the selected values are managed as an array.
     * Single-select mode is the default behavior where only one option can be chosen at a time.
     *
     * @default false
     */
    isMultiSelect?: boolean;
    /**
     * The unique name identifier for the select input used in form handling and state management.
     * This name serves as a key for identifying the input within forms and is essential for proper form data collection.
     * It should be unique within the form context to avoid conflicts with other form elements.
     */
    name: string;
    /**
     * Callback function triggered when the selection changes, receiving the new value(s) as a parameter.
     * The function is called whenever a user makes a selection or deselection, providing the updated value for external state management.
     * The parameter type matches the selection mode - a single string for single-select or an array for multi-select scenarios.
     */
    onChange(value: any): void;
    /**
     * Array of option objects containing label and value pairs that define the available choices in the dropdown.
     * Each option object must include both a display label for user-facing text and a value for internal data handling.
     * The options array determines what choices are presented to users and how they are processed when selected.
     */
    options: Option[];
    /**
     * The horizontal position of the dropdown relative to the input, determining where the option list appears.
     * This property controls the alignment of the dropdown popover, with 'left' aligning to the left edge and 'right' to the right edge.
     * The positioning helps ensure the dropdown remains visible within the viewport and aligns properly with the design layout.
     *
     * @default 'right'
     */
    popOverPosition?: 'left' | 'right';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'SelectInput'
     */
    testId?: string;
}

/**
 * The `SelectInput` component provides a customizable dropdown selection interface that allows users to choose from predefined options.
 * It supports both single and multi-select functionality with smooth animations and accessibility features. The component integrates
 * with MobX for state management and provides visual feedback for user interactions including hover states and selection indicators.
 *
 * @param props                 The component props.
 * @param props.defaultValue    The initial value(s) for the select input, can be a single string or array of strings for multi-select.
 * @param props.filterLabel     The display label shown in the select input when closed.
 * @param props.isDisabled      Whether the select input is disabled and non-interactive.
 * @param props.isMultiSelect   Whether multiple options can be selected simultaneously.
 * @param props.name            The unique name identifier for the select input used in form handling.
 * @param props.onChange        Callback function triggered when the selection changes, receives the new value(s).
 * @param props.options         Array of option objects containing label and value pairs for the dropdown.
 * @param props.popOverPosition The horizontal position of the dropdown relative to the input ('left' or 'right').
 * @param props.testId          Test identifier for component targeting in automated tests.
 * @returns The SelectInput component JSX with dropdown functionality and animations.
 *
 * @example
 * ```tsx
 * <SelectInput
 *     defaultValue="option1"
 *     filterLabel="Choose an option"
 *     name="selectInput"
 *     onChange={(value) => console.log(value)}
 *     options={[
 *         {label: 'Option 1', value: 'option1'},
 *         {label: 'Option 2', value: 'option2'}
 *     ]}
 *     testId="my-select-input"
 * />
 * ```
 */
const SelectInput = observer(({
    defaultValue,
    filterLabel,
    isDisabled = false,
    isMultiSelect = false,
    name,
    onChange,
    options,
    popOverPosition = 'right',
    testId = 'SelectInput'
}: ComponentProps) => {
    const {
        color,
        handleChangeValue,
        handleOpenSelect,
        isOpen,
        isTop,
        listHeight,
        selectRef,
        value: selectedValue
    } = useSelectInput(name, defaultValue, options, onChange, isDisabled, isMultiSelect);

    return (
        <Wrapper ref={selectRef} $isDisabled={isDisabled} data-cy={testId} onClick={handleOpenSelect}>
            <P $size="large">
                <Color $color={color}>{filterLabel}</Color>
            </P>
            <Spacer x={2} isNotStretching />
            <StyledChevron $isOpen={isOpen} color1={color} height={16} width={16} />
            <AnimatePresence mode="wait">
                {isOpen && (
                    <OptionList
                        height={listHeight}
                        isOnTop={isTop}
                        position={popOverPosition}
                        testId={`${testId}-list`}
                    >
                        {options.map(({label, value}) => {
                            let isActive = false;

                            if (isMultiSelect && Array.isArray(selectedValue)) {
                                isActive = selectedValue.includes(value);
                            } else {
                                isActive = selectedValue === value;
                            }

                            return (
                                <OptionItem
                                    key={value}
                                    index={String(value)}
                                    isActive={isActive}
                                    label={label}
                                    isSelectable
                                    onClick={handleChangeValue}
                                />
                            );
                        })}
                    </OptionList>
                )}
            </AnimatePresence>
        </Wrapper>
    );
});

SelectInput.displayName = 'SelectInput';

export {SelectInput};

interface WrapperProps {
    $isDisabled: boolean;
}

const Wrapper = styled.div<WrapperProps>`
    align-items: center;
    cursor: ${({$isDisabled}) => ($isDisabled ? 'not-allowed' : 'pointer')};
    display: flex;
    justify-content: space-between;
    position: relative;
`;

interface ChevronDownProps {
    $isOpen: boolean;
}

const StyledChevron = styled(ChevronDown, transient)<ChevronDownProps>`
    transform: ${({$isOpen}) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.3s ease-in-out;
`;